import { apiClient } from './apiClient'
import type { Quote, QuoteItem, NoteStatus } from '@/types'

// ============================================
// Backend ↔ Frontend field mapping
// ============================================

interface DbQuote {
  id: string
  client_id: string
  client_name: string
  client_fresh_id: string
  contact_id: string
  contact_name: string
  status: string
  items: string // JSON
  total_ht: number
  total_ttc: number
  notes: string
  urgency: string
  delivery_date: string
  show_delivery: number
  created_by: string
  created_at: number
  updated_at: number
  synced_at: number | null
  fresh_id: string | null
  fresh_ref: string | null
  sync_failed: number
  sync_error_message: string | null
  note_ref: string | null
}

function dbToQuote(db: DbQuote): Quote {
  return {
    id: db.id,
    clientId: db.client_id,
    client: db.client_name ? {
      id: db.client_id,
      name: db.client_name,
      siret: '',
      freshId: db.client_fresh_id || undefined,
      createdAt: 0,
      updatedAt: 0
    } : undefined,
    contactId: db.contact_id || undefined,
    contactName: db.contact_name || undefined,
    status: db.status as Quote['status'],
    items: JSON.parse(db.items || '[]') as QuoteItem[],
    totalHT: db.total_ht,
    totalTTC: db.total_ttc,
    notes: db.notes || undefined,
    showDelivery: db.show_delivery === 1,
    urgency: (db.urgency as Quote['urgency']) || undefined,
    deliveryDate: db.delivery_date || undefined,
    createdBy: db.created_by,
    createdAt: db.created_at,
    updatedAt: db.updated_at,
    syncedAt: db.synced_at || undefined,
    freshId: db.fresh_id || undefined,
    freshRef: db.fresh_ref || undefined,
    syncFailed: db.sync_failed === 1,
    syncErrorMessage: db.sync_error_message || undefined,
    noteRef: db.note_ref || undefined
  }
}

function quoteToDb(q: Quote): Record<string, unknown> {
  return {
    id: q.id,
    client_id: q.clientId || '',
    client_name: q.client?.name || '',
    client_fresh_id: q.client?.freshId || '',
    contact_id: q.contactId || '',
    contact_name: q.contactName || '',
    status: q.status,
    items: q.items,
    total_ht: q.totalHT,
    total_ttc: q.totalTTC,
    notes: q.notes || '',
    urgency: q.urgency || 'standard',
    delivery_date: q.deliveryDate || '',
    show_delivery: q.showDelivery || false
  }
}

// ============================================
// API functions
// ============================================

export async function fetchQuotes(status?: string): Promise<{ quotes: Quote[]; pending: number; notesPending: number }> {
  const url = status ? `/quotes?status=${status}` : '/quotes'
  const res = await apiClient.get<{ data: DbQuote[]; meta: { total: number; pending: number; notesPending?: number } }>(url)
  return {
    quotes: (res.data || []).map(dbToQuote),
    pending: res.meta?.pending || 0,
    notesPending: res.meta?.notesPending || 0
  }
}

export async function fetchQuote(id: string): Promise<Quote | null> {
  try {
    const res = await apiClient.get<{ data: DbQuote }>(`/quotes/${id}`)
    return dbToQuote(res.data)
  } catch {
    return null
  }
}

export async function createQuoteApi(quote: Quote): Promise<Quote> {
  const res = await apiClient.post<{ data: DbQuote }>('/quotes', quoteToDb(quote))
  return dbToQuote(res.data)
}

export async function updateQuoteApi(id: string, updates: Record<string, unknown>): Promise<Quote> {
  const res = await apiClient.put<{ data: DbQuote }>(`/quotes/${id}`, updates)
  return dbToQuote(res.data)
}

export async function submitQuoteApi(id: string): Promise<Quote> {
  const res = await apiClient.put<{ data: DbQuote }>(`/quotes/${id}/submit`, {})
  return dbToQuote(res.data)
}

export async function validateQuoteApi(id: string): Promise<Quote> {
  const res = await apiClient.put<{ data: DbQuote }>(`/quotes/${id}/validate`, {})
  return dbToQuote(res.data)
}

export async function rejectQuoteApi(id: string, reason: string): Promise<Quote> {
  const res = await apiClient.put<{ data: DbQuote }>(`/quotes/${id}/reject`, { reason })
  return dbToQuote(res.data)
}

export async function sendToPricingApi(id: string): Promise<Quote> {
  const res = await apiClient.put<{ data: DbQuote }>(`/quotes/${id}/send-to-pricing`, {})
  return dbToQuote(res.data)
}

export async function setNoteStatusApi(id: string, status: NoteStatus, noteRef?: string): Promise<Quote> {
  const body: { status: NoteStatus; note_ref?: string } = { status }
  if (noteRef !== undefined) body.note_ref = noteRef
  const res = await apiClient.put<{ data: DbQuote }>(`/quotes/${id}/note-status`, body)
  return dbToQuote(res.data)
}

export async function deleteQuoteApi(id: string): Promise<void> {
  await apiClient.delete(`/quotes/${id}`)
}
